import {
  useMutation,
  UseMutationResult,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import type {
  ResponseDto,
  MembershipTierDto,
  CreateMembershipTierDto,
} from "@mimber/schema"
import { membershipTiersKeys } from "../membership-tiers.utils"

/**
 * Organization-specific hook for creating a membership tier
 * Can only be used in mimber-organization app
 */
export const useCreateMembershipTier = (
  options?: UseMutationOptions<
    ResponseDto<MembershipTierDto>,
    Error,
    CreateMembershipTierDto
  >
): UseMutationResult<
  ResponseDto<MembershipTierDto>,
  Error,
  CreateMembershipTierDto
> => {
  guardAppUsage("mimber-organization", "useCreateMembershipTier")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (
      data: CreateMembershipTierDto
    ): Promise<ResponseDto<MembershipTierDto>> => {
      const response = await server.post<ResponseDto<MembershipTierDto>>(
        "membership-tiers",
        data
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      // Invalidate all membership tiers queries (regardless of params)
      queryClient.invalidateQueries({
        queryKey: membershipTiersKeys.findAll(),
      })

      if (options?.onSuccess) {
        options.onSuccess(response, variables, onMutateResult, context)
      }
    },
  })
}
